import React from 'react';
import {
    Campaign as CampaignIcon,
    Dashboard as DashboardIcon,
    VideoLibrary as VideoLibraryIcon,
    Sensors as SensorsIcon,
    SmartDisplay as ClipsIcon,
} from '@mui/icons-material';
import { ROUTES_NAMES } from '../../components/routes';
import i18n from '../../i18n';

/** Explorer section ids. 0/2/3/4 double as permission indexes for hasExplorerTabAccess. */
export const EXPLORER_PROGRAMS_TAB = 0;
export const EXPLORER_BRANDS_TAB = 2;
export const EXPLORER_LIVESTREAMS_TAB = 3;
/** Legacy "Comparar" permission; grants Programas in compare mode. */
export const EXPLORER_LEGACY_COMPARE_TAB = 4;
/** Not a permission index; shown when the user can open any other section. */
export const EXPLORER_GENERAL_TAB = 5;
/** Granted by the company `clips` product, not by other Explorer tabs. */
export const EXPLORER_SOCIAL_TAB = 6;

type HasExplorerTabAccess = (tabIndex: number) => boolean;

export interface ExplorerTabDef {
    index: number;
    labelKey: 'tabs.panel' | 'tabs.sponsors' | 'tabs.channels' | 'tabs.livestreams' | 'tabs.clips';
    /** Spanish label. Prefer `t(labelKey)` from the explorer namespace. */
    label: string;
    path: string;
    icon: React.ElementType;
    canAccess: (hasAccess: HasExplorerTabAccess) => boolean;
}

export const EXPLORER_TABS: ExplorerTabDef[] = [
    {
        index: EXPLORER_GENERAL_TAB,
        labelKey: 'tabs.panel',
        label: 'Mi panel',
        path: ROUTES_NAMES.EXPLORER_PANEL,
        icon: DashboardIcon,
        canAccess: (has) =>
            has(EXPLORER_BRANDS_TAB) || has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LIVESTREAMS_TAB),
    },
    {
        index: EXPLORER_BRANDS_TAB,
        labelKey: 'tabs.sponsors',
        label: 'Auspiciadores',
        path: ROUTES_NAMES.EXPLORER_BRANDS_LIST,
        icon: CampaignIcon,
        canAccess: (has) => has(EXPLORER_BRANDS_TAB),
    },
    {
        index: EXPLORER_PROGRAMS_TAB,
        labelKey: 'tabs.channels',
        label: 'Canales',
        path: ROUTES_NAMES.EXPLORER_PROGRAMS_LIST,
        icon: VideoLibraryIcon,
        canAccess: (has) => has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB),
    },
    {
        index: EXPLORER_SOCIAL_TAB,
        labelKey: 'tabs.clips',
        label: 'Clips',
        path: ROUTES_NAMES.EXPLORER_SOCIAL_LIST,
        icon: ClipsIcon,
        canAccess: (has) => has(EXPLORER_SOCIAL_TAB),
    },
    {
        index: EXPLORER_LIVESTREAMS_TAB,
        labelKey: 'tabs.livestreams',
        label: 'Livestreams',
        path: ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST,
        icon: SensorsIcon,
        canAccess: (has) => has(EXPLORER_LIVESTREAMS_TAB),
    },
];

export const SPONSORSHIPS_REPORT_TYPE = 'explorer.sponsorships';
export const SPONSORSHIPS_REPORT_LABEL = 'Auspicios por programa';
export const SPONSORSHIPS_REPORT_DESCRIPTION = 'Cada mención con link al video.';

export const LIVESTREAMS_CHANNELS_REPORT_TYPE = 'explorer.livestreams.channels';
export const LIVESTREAMS_PROGRAMS_REPORT_TYPE = 'explorer.livestreams.programs';
/** Livestreams export cards in dialog order. */
export const LIVESTREAMS_EXPORT_REPORTS = [LIVESTREAMS_CHANNELS_REPORT_TYPE, LIVESTREAMS_PROGRAMS_REPORT_TYPE];
/** Livestream channel detail: one row per stream of that channel. */
export const LIVESTREAM_CHANNEL_REPORT_TYPE = 'explorer.livestreams.channel';
/** Livestream program detail: one row per stream of that program. */
export const LIVESTREAM_PROGRAM_REPORT_TYPE = 'explorer.livestreams.program';
/** `programIds` filter value for streams with no program. */
export const LIVESTREAM_UNASSIGNED_PROGRAM = 'none';

export const AUDIENCE_PROGRAMS_REPORT_TYPE = 'explorer.livestreams.audience.programs';
export const AUDIENCE_STREAMS_REPORT_TYPE = 'explorer.livestreams.audience.streams';
/** Mapa de audiencia export cards in dialog order. */
export const AUDIENCE_EXPORT_REPORTS = [AUDIENCE_PROGRAMS_REPORT_TYPE, AUDIENCE_STREAMS_REPORT_TYPE];
/** Livestream competition: one row per stream in the clock window. */
export const COMPETITION_REPORT_TYPE = 'explorer.livestreams.competition';
/** Auspiciadores clips: one row per brand with clip metrics. */
export const SOCIAL_BRANDS_REPORT_TYPE = 'explorer.social.brands';
/** Clips ranking: one row per channel. */
export const SOCIAL_CHANNELS_REPORT_TYPE = 'explorer.social.channels';
/** One channel's clips and posts for the selected period. */
export const SOCIAL_CHANNEL_REPORT_TYPE = 'explorer.social.channel';
/** Canales hub: On Demand + Livestreams + Clips in one XLSX. */
export const CANALES_BUNDLE_REPORT_TYPE = 'explorer.canales';
export const CANALES_SECTION_ONDEMAND = 'ondemand';
export const CANALES_SECTION_LIVESTREAMS = 'livestreams';
export const CANALES_SECTION_CLIPS = 'clips';

const EXPORT_REPORT_CARD_KEYS: Record<string, { labelKey: string; descriptionKey: string }> = {
    'explorer.programs': {
        labelKey: 'export.channelsAndPrograms',
        descriptionKey: 'export.channelsAndProgramsDescription',
    },
    'explorer.brands': {
        labelKey: 'export.sponsors',
        descriptionKey: 'export.sponsorsDescription',
    },
    [SPONSORSHIPS_REPORT_TYPE]: {
        labelKey: 'export.sponsorships',
        descriptionKey: 'export.sponsorshipsDescription',
    },
    [LIVESTREAMS_CHANNELS_REPORT_TYPE]: {
        labelKey: 'export.livestreamChannels',
        descriptionKey: 'export.livestreamChannelsDescription',
    },
    [LIVESTREAMS_PROGRAMS_REPORT_TYPE]: {
        labelKey: 'export.livestreamPrograms',
        descriptionKey: 'export.livestreamProgramsDescription',
    },
    [LIVESTREAM_CHANNEL_REPORT_TYPE]: {
        labelKey: 'export.channelStreams',
        descriptionKey: 'export.channelStreamsDescription',
    },
    [LIVESTREAM_PROGRAM_REPORT_TYPE]: {
        labelKey: 'export.programStreams',
        descriptionKey: 'export.programStreamsDescription',
    },
    [AUDIENCE_PROGRAMS_REPORT_TYPE]: {
        labelKey: 'export.audienceChannels',
        descriptionKey: 'export.audienceChannelsDescription',
    },
    [AUDIENCE_STREAMS_REPORT_TYPE]: {
        labelKey: 'export.audienceStreams',
        descriptionKey: 'export.audienceStreamsDescription',
    },
    [COMPETITION_REPORT_TYPE]: {
        labelKey: 'export.competition',
        descriptionKey: 'export.competitionDescription',
    },
    [SOCIAL_BRANDS_REPORT_TYPE]: {
        labelKey: 'export.clipSponsors',
        descriptionKey: 'export.clipSponsorsDescription',
    },
    [SOCIAL_CHANNELS_REPORT_TYPE]: {
        labelKey: 'export.clipChannels',
        descriptionKey: 'export.clipChannelsDescription',
    },
    [SOCIAL_CHANNEL_REPORT_TYPE]: {
        labelKey: 'export.clipChannel',
        descriptionKey: 'export.clipChannelDescription',
    },
};

/** Report cards of the export dialog. Chat messages store older labels, so cards are relabeled from here. */
export function exportReportCard(reportType: string): { label: string; description: string } {
    const keys = EXPORT_REPORT_CARD_KEYS[reportType];
    if (!keys) return { label: reportType, description: '' };
    return {
        label: i18n.t(keys.labelKey, { ns: 'explorer' }),
        description: i18n.t(keys.descriptionKey, { ns: 'explorer' }),
    };
}

/** @deprecated Use exportReportCard so the active language applies. */
export const EXPORT_REPORT_CARDS: Record<string, { label: string; description: string }> = {
    'explorer.programs': {
        label: 'On Demand',
        description: 'Una fila por programa con métricas On Demand del período.',
    },
    'explorer.brands': { label: 'Auspiciadores', description: 'Una fila por marca.' },
    [SPONSORSHIPS_REPORT_TYPE]: {
        label: SPONSORSHIPS_REPORT_LABEL,
        description: SPONSORSHIPS_REPORT_DESCRIPTION,
    },
    [LIVESTREAMS_CHANNELS_REPORT_TYPE]: {
        label: 'Canales',
        description: 'Una fila por canal con métricas de livestreams del período.',
    },
    [LIVESTREAMS_PROGRAMS_REPORT_TYPE]: {
        label: 'Programas',
        description: 'Una fila por programa con métricas de livestreams del período.',
    },
    [LIVESTREAM_CHANNEL_REPORT_TYPE]: {
        label: 'Livestreams del canal',
        description: 'Una fila por livestream del canal. Un programa elegido incluye métricas diarias y espectadores en vivo.',
    },
    [LIVESTREAM_PROGRAM_REPORT_TYPE]: {
        label: 'Livestreams del programa',
        description: 'Una fila por livestream del programa, más métricas diarias y espectadores en vivo.',
    },
    [AUDIENCE_PROGRAMS_REPORT_TYPE]: {
        label: 'Canales y programas',
        description: 'Mapa de audiencia y ranking de canales y programas en las franjas elegidas.',
    },
    [AUDIENCE_STREAMS_REPORT_TYPE]: {
        label: 'Canales y streams',
        description: 'Mapa de audiencia y ranking de canales y streams en las franjas elegidas.',
    },
    [COMPETITION_REPORT_TYPE]: {
        label: 'Competencia',
        description: 'Una fila por livestream de la franja, con promedio, espectadores ahora, peak y duración.',
    },
    [SOCIAL_BRANDS_REPORT_TYPE]: {
        label: 'Auspiciadores clips',
        description: 'Una fila por marca con métricas de clips del período.',
    },
    [SOCIAL_CHANNELS_REPORT_TYPE]: {
        label: 'Clips',
        description: 'Una fila por canal con métricas de clips del período.',
    },
    [SOCIAL_CHANNEL_REPORT_TYPE]: {
        label: 'Clips del canal',
        description: 'Todo el contenido del canal: clips, posts y el desglose por plataforma.',
    },
};

/** The brand × program report needs both Auspiciadores and Programas. */
export function canExportSponsorships(has: HasExplorerTabAccess): boolean {
    return has(EXPLORER_BRANDS_TAB) && (has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB));
}

/** Export reports the user's Explorer sections allow, in dialog order. */
export function accessibleExportReports(has: HasExplorerTabAccess): string[] {
    return [
        ...(has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB) ? ['explorer.programs'] : []),
        ...(has(EXPLORER_BRANDS_TAB) ? ['explorer.brands'] : []),
        ...(canExportSponsorships(has) ? [SPONSORSHIPS_REPORT_TYPE] : []),
    ];
}

/**
 * Reports a detail page can open preselected on its entity, own report first. Auspiciadores
 * only filters by brand and On Demand only by channel or program.
 */
export function entityExportReports(has: HasExplorerTabAccess, entity: 'channel' | 'program' | 'brand'): string[] {
    const accessible = accessibleExportReports(has);
    return entity === 'brand'
        ? accessible.filter((r) => r !== 'explorer.programs')
        : accessible.filter((r) => r !== 'explorer.brands');
}

/** Export dialog report cards for the given report types. */
export function exportAlternatives(reportTypes: string[]) {
    return reportTypes.map((reportType) => ({ reportType, ...exportReportCard(reportType) }));
}

export function getAccessibleExplorerTabs(hasAccess: HasExplorerTabAccess): ExplorerTabDef[] {
    return EXPLORER_TABS.filter((tab) => tab.canAccess(hasAccess));
}

export type ExplorerMediaFormat = 'ondemand' | 'livestreams' | 'clips';

/** Flat or nested clips brand detail (not the Auspiciadores clips list). */
export function isExplorerClipsBrandPath(pathname: string): boolean {
    if (pathname.startsWith(`${ROUTES_NAMES.EXPLORER}/clips/brands/`)) return true;
    if (pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS) return false;
    return pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`);
}

/** Clips IA Canales hub only — not `/canales/ondemand|livestreams|clips`. */
export function isExplorerCanalesHubPath(pathname: string): boolean {
    return pathname === ROUTES_NAMES.EXPLORER_CANALES || pathname === `${ROUTES_NAMES.EXPLORER_CANALES}/`;
}

/** Nested format list or entity detail under Canales (Clips IA). */
export function isExplorerCanalesFormatPath(pathname: string): boolean {
    return (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND ||
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS ||
        pathname === ROUTES_NAMES.EXPLORER_CANALES_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND}/`) ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS}/`) ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_CLIPS}/`)
    );
}

/** True for nested or flat livestream rating pages (incl. legacy /competencia). */
export function isExplorerLivestreamCompetitionPath(pathname: string): boolean {
    return (
        pathname === ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION ||
        pathname === ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION_LEGACY ||
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAM_COMPETITION ||
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAM_COMPETITION_LEGACY
    );
}

/** `/competencia` → `/rating`, preserving Canales nesting for Clips IA. */
export function explorerCompetitionLegacyRedirect(
    pathname: string,
    clipsIa: boolean,
): string | null {
    const isLegacy =
        pathname === ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION_LEGACY ||
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAM_COMPETITION_LEGACY;
    if (!isLegacy) return null;
    return clipsIa
        ? ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAM_COMPETITION
        : ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION;
}

/** Nested Auspiciadores list or detail under a format (Clips IA). */
export function isExplorerAuspiciadoresFormatPath(pathname: string): boolean {
    return (
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND ||
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND}/`) ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`)
    );
}

/**
 * Clips IA: old flat list → nested canonical. `null` = keep the flat URL
 * (no Clips IA, or path is already nested / not a list fallback).
 */
export function explorerClipsIaListRedirect(
    pathname: string,
    clipsIa: boolean,
    sponsorMediaFormat: 'ondemand' | 'clips' = 'ondemand',
): string | null {
    if (!clipsIa) return null;
    if (pathname === ROUTES_NAMES.EXPLORER_BRANDS_LIST) {
        return sponsorMediaFormat === 'clips'
            ? ROUTES_NAMES.EXPLORER_BRANDS_CLIPS
            : ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_PROGRAMS_LIST) {
        return ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST) {
        return ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_SOCIAL_LIST) {
        return ROUTES_NAMES.EXPLORER_CANALES_CLIPS;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_BRAND_MOVERS) {
        return ROUTES_NAMES.EXPLORER_BRAND_MOVERS_ONDEMAND;
    }
    return null;
}

/**
 * Clips IA brand-detail fallbacks (both directions).
 * Flat legacy paths redirect to nested when Clips is on; nested → flat when off.
 */
export function explorerBrandDetailRedirect(
    pathname: string,
    clipsIa: boolean,
): string | null {
    const odNestedPrefix = `${ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND}/`;
    const clipsNestedPrefix = `${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`;
    const flatBrandPrefix = `${ROUTES_NAMES.EXPLORER}/brands/`;
    const flatClipsBrandPrefix = `${ROUTES_NAMES.EXPLORER_SOCIAL_LIST}/brands/`;

    if (clipsIa) {
        if (pathname.startsWith(flatBrandPrefix)) {
            const id = pathname.slice(flatBrandPrefix.length);
            if (id && id !== 'movers' && !id.includes('/')) {
                return `${ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND}/${id}`;
            }
        }
        if (pathname.startsWith(flatClipsBrandPrefix)) {
            const id = pathname.slice(flatClipsBrandPrefix.length);
            if (id && !id.includes('/')) {
                return `${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/${id}`;
            }
        }
        return null;
    }

    if (pathname.startsWith(odNestedPrefix)) {
        const id = pathname.slice(odNestedPrefix.length);
        if (id === 'movers') return ROUTES_NAMES.EXPLORER_BRAND_MOVERS;
        if (id && !id.includes('/')) return `${ROUTES_NAMES.EXPLORER}/brands/${id}`;
    }
    if (pathname.startsWith(clipsNestedPrefix)) {
        const id = pathname.slice(clipsNestedPrefix.length);
        if (id && !id.includes('/')) {
            return `${ROUTES_NAMES.EXPLORER_SOCIAL_LIST}/brands/${id}`;
        }
    }
    return null;
}

/**
 * Clips IA Canales entity-detail fallbacks (program / podcast / livestream / clips channel).
 * Flat ↔ nested under `/canales/{format}/…`.
 */
export function explorerCanalesDetailRedirect(
    pathname: string,
    clipsIa: boolean,
): string | null {
    const flatLive = ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST;
    const nestedLive = ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS;
    const flatClips = ROUTES_NAMES.EXPLORER_SOCIAL_LIST;
    const nestedClips = ROUTES_NAMES.EXPLORER_CANALES_CLIPS;

    const od = ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND;
    if (clipsIa) {
        if (pathname.startsWith(`${ROUTES_NAMES.EXPLORER}/programs/`)) {
            return pathname.replace(
                `${ROUTES_NAMES.EXPLORER}/programs/`,
                `${od}/programs/`,
            );
        }
        if (pathname.startsWith(`${ROUTES_NAMES.EXPLORER}/podcasts/`)) {
            const id = pathname.slice(`${ROUTES_NAMES.EXPLORER}/podcasts/`.length);
            if (id && !id.includes('/')) return `${od}/${id}`;
        }
        // Legacy `/canales/ondemand/podcasts/:id` → `/canales/ondemand/:id`
        if (pathname.startsWith(`${od}/podcasts/`)) {
            const id = pathname.slice(`${od}/podcasts/`.length);
            if (id && !id.includes('/')) return `${od}/${id}`;
        }
        if (pathname.startsWith(`${flatLive}/`)) {
            const rest = pathname.slice(flatLive.length);
            const nested = `${nestedLive}${rest === '/competencia' ? '/rating' : rest}`;
            return nested;
        }
        // Clips channel detail — not `/clips/brands/…` (Auspiciadores).
        if (
            pathname.startsWith(`${flatClips}/`) &&
            !pathname.startsWith(`${flatClips}/brands`)
        ) {
            return `${nestedClips}${pathname.slice(flatClips.length)}`;
        }
        return null;
    }

    if (pathname.startsWith(`${od}/programs/`)) {
        return pathname.replace(`${od}/programs/`, `${ROUTES_NAMES.EXPLORER}/programs/`);
    }
    if (pathname.startsWith(`${od}/podcasts/`)) {
        return pathname.replace(`${od}/podcasts/`, `${ROUTES_NAMES.EXPLORER}/podcasts/`);
    }
    // `/canales/ondemand/:podcastId` (not programs / podcasts / list)
    if (pathname.startsWith(`${od}/`)) {
        const rest = pathname.slice(`${od}/`.length);
        if (rest && !rest.includes('/') && rest !== 'programs' && rest !== 'podcasts') {
            return `${ROUTES_NAMES.EXPLORER}/podcasts/${rest}`;
        }
    }
    if (pathname.startsWith(`${nestedLive}/`)) {
        return `${flatLive}${pathname.slice(nestedLive.length)}`;
    }
    if (pathname.startsWith(`${nestedClips}/`)) {
        return `${flatClips}${pathname.slice(nestedClips.length)}`;
    }
    return null;
}

/** Brand or Canales entity detail redirect for Clips IA fallbacks. */
export function explorerEntityDetailRedirect(
    pathname: string,
    clipsIa: boolean,
): string | null {
    return (
        explorerCompetitionLegacyRedirect(pathname, clipsIa) ??
        explorerBrandDetailRedirect(pathname, clipsIa) ??
        explorerCanalesDetailRedirect(pathname, clipsIa)
    );
}

/** Flat fallback for a nested Clips IA list path when Clips is off. */
export function explorerNestedListFlatFallback(pathname: string): string | null {
    if (pathname === ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND) {
        return ROUTES_NAMES.EXPLORER_BRANDS_LIST;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS) {
        return ROUTES_NAMES.EXPLORER_BRANDS_LIST;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND) {
        return ROUTES_NAMES.EXPLORER_PROGRAMS_LIST;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS) {
        return ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_CANALES_CLIPS) {
        return ROUTES_NAMES.EXPLORER_SOCIAL_LIST;
    }
    if (pathname === ROUTES_NAMES.EXPLORER_BRAND_MOVERS_ONDEMAND) {
        return ROUTES_NAMES.EXPLORER_BRAND_MOVERS;
    }
    return null;
}

/** Sponsors format from path when Clips IA nested URLs are in use. */
export function getExplorerSponsorsFormatFromPath(
    pathname: string,
): 'ondemand' | 'clips' | null {
    if (
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`) ||
        isExplorerClipsBrandPath(pathname)
    ) {
        return 'clips';
    }
    if (
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND}/`) ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER}/brands/`) ||
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_LIST
    ) {
        return 'ondemand';
    }
    return null;
}

/** Company can open the Canales hub when any format child is available. */
export function canAccessExplorerCanalesHub(hasAccess: HasExplorerTabAccess): boolean {
    return (
        hasAccess(EXPLORER_PROGRAMS_TAB) ||
        hasAccess(EXPLORER_LEGACY_COMPARE_TAB) ||
        hasAccess(EXPLORER_LIVESTREAMS_TAB) ||
        hasAccess(EXPLORER_SOCIAL_TAB)
    );
}

/** Format section cards for the Canales hub bundle export, permission-gated. */
export function canalesHubSectionChoices(hasAccess: HasExplorerTabAccess): Array<{
    key: string;
    label: string;
    description: string;
}> {
    const sections: Array<{ key: string; labelKey: string; descriptionKey: string }> = [
        ...(hasAccess(EXPLORER_PROGRAMS_TAB) || hasAccess(EXPLORER_LEGACY_COMPARE_TAB)
            ? [
                  {
                      key: CANALES_SECTION_ONDEMAND,
                      labelKey: 'export.canalesSectionOnDemand',
                      descriptionKey: 'export.canalesSectionOnDemandDescription',
                  },
              ]
            : []),
        ...(hasAccess(EXPLORER_LIVESTREAMS_TAB)
            ? [
                  {
                      key: CANALES_SECTION_LIVESTREAMS,
                      labelKey: 'export.canalesSectionLivestreams',
                      descriptionKey: 'export.canalesSectionLivestreamsDescription',
                  },
              ]
            : []),
        ...(hasAccess(EXPLORER_SOCIAL_TAB)
            ? [
                  {
                      key: CANALES_SECTION_CLIPS,
                      labelKey: 'export.canalesSectionClips',
                      descriptionKey: 'export.canalesSectionClipsDescription',
                  },
              ]
            : []),
    ];
    return sections.map((section) => ({
        key: section.key,
        label: i18n.t(section.labelKey, { ns: 'explorer' }),
        description: i18n.t(section.descriptionKey, { ns: 'explorer' }),
    }));
}

export type ExplorerSidebarFormat = ExplorerMediaFormat;

export type ExplorerSidebarChildDef = {
    format: ExplorerSidebarFormat;
    labelKey: 'format.ondemand' | 'format.livestreams' | 'format.clips';
    path: string;
};

export type ExplorerSidebarGroupDef = {
    index: number;
    labelKey: 'tabs.channels' | 'tabs.sponsors';
    children: ExplorerSidebarChildDef[];
};

function explorerSidebarAccessMap(hasAccess: HasExplorerTabAccess) {
    const tabs = getAccessibleExplorerTabs(hasAccess).filter(
        (tab) => tab.index !== EXPLORER_GENERAL_TAB,
    );
    return new Map(tabs.map((tab) => [tab.index, tab]));
}

/** Sidebar items. Clips IA collapses Livestreams and Clips into the Canales / Auspiciadores pillars. */
export function getExplorerSidebarTabs(
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): ExplorerTabDef[] {
    const tabs = getAccessibleExplorerTabs(hasAccess).filter(
        (tab) => tab.index !== EXPLORER_GENERAL_TAB,
    );
    if (!clipsIa) return tabs;

    const byIndex = explorerSidebarAccessMap(hasAccess);
    const programs = byIndex.get(EXPLORER_PROGRAMS_TAB);
    const livestreams = byIndex.get(EXPLORER_LIVESTREAMS_TAB);
    const clips = byIndex.get(EXPLORER_SOCIAL_TAB);
    const brands = byIndex.get(EXPLORER_BRANDS_TAB);
    const items: ExplorerTabDef[] = [];

    if (programs || livestreams || clips) {
        items.push({
            index: EXPLORER_PROGRAMS_TAB,
            labelKey: 'tabs.channels',
            label: 'Canales',
            path: ROUTES_NAMES.EXPLORER_CANALES,
            icon: VideoLibraryIcon,
            canAccess: () => true,
        });
    }
    if (brands || clips) {
        items.push({
            index: EXPLORER_BRANDS_TAB,
            labelKey: 'tabs.sponsors',
            label: 'Auspiciadores',
            path: brands
                ? ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND
                : ROUTES_NAMES.EXPLORER_BRANDS_CLIPS,
            icon: CampaignIcon,
            canAccess: () => true,
        });
    }
    return items;
}

/** Nested Explorador groups for Clips IA. Empty when the company does not use that IA. */
export function getExplorerSidebarGroups(
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): ExplorerSidebarGroupDef[] {
    if (!clipsIa) return [];
    const byIndex = explorerSidebarAccessMap(hasAccess);
    const programs = byIndex.get(EXPLORER_PROGRAMS_TAB);
    const livestreams = byIndex.get(EXPLORER_LIVESTREAMS_TAB);
    const clips = byIndex.get(EXPLORER_SOCIAL_TAB);
    const brands = byIndex.get(EXPLORER_BRANDS_TAB);
    const groups: ExplorerSidebarGroupDef[] = [];

    const channelChildren: ExplorerSidebarChildDef[] = [];
    if (programs) {
        channelChildren.push({
            format: 'ondemand',
            labelKey: 'format.ondemand',
            path: ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND,
        });
    }
    if (livestreams) {
        channelChildren.push({
            format: 'livestreams',
            labelKey: 'format.livestreams',
            path: ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS,
        });
    }
    if (clips) {
        channelChildren.push({
            format: 'clips',
            labelKey: 'format.clips',
            path: ROUTES_NAMES.EXPLORER_CANALES_CLIPS,
        });
    }
    if (channelChildren.length > 0) {
        groups.push({
            index: EXPLORER_PROGRAMS_TAB,
            labelKey: 'tabs.channels',
            children: channelChildren,
        });
    }

    const sponsorChildren: ExplorerSidebarChildDef[] = [];
    if (brands) {
        sponsorChildren.push({
            format: 'ondemand',
            labelKey: 'format.ondemand',
            path: ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND,
        });
    }
    if (clips) {
        sponsorChildren.push({
            format: 'clips',
            labelKey: 'format.clips',
            path: ROUTES_NAMES.EXPLORER_BRANDS_CLIPS,
        });
    }
    if (sponsorChildren.length > 0) {
        groups.push({
            index: EXPLORER_BRANDS_TAB,
            labelKey: 'tabs.sponsors',
            children: sponsorChildren,
        });
    }
    return groups;
}

export function isExplorerSidebarChildActive(
    child: ExplorerSidebarChildDef,
    pathname: string,
): boolean {
    // Auspiciadores format children — path-driven (list + detail).
    if (
        child.path === ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND ||
        child.path === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS
    ) {
        const sponsorsFormat = getExplorerSponsorsFormatFromPath(pathname);
        if (!sponsorsFormat) return false;
        return (
            (child.format === 'ondemand' && sponsorsFormat === 'ondemand') ||
            (child.format === 'clips' && sponsorsFormat === 'clips')
        );
    }
    // Hub highlights the Canales group header, not On demand / Livestreams / Clips.
    if (isExplorerCanalesHubPath(pathname)) return false;
    if (isExplorerClipsBrandPath(pathname)) return false;
    const tab = getActiveExplorerTab(pathname);
    if (child.format === 'ondemand') return tab === EXPLORER_PROGRAMS_TAB;
    if (child.format === 'livestreams') return tab === EXPLORER_LIVESTREAMS_TAB;
    return tab === EXPLORER_SOCIAL_TAB;
}

export function getExplorerMediaFormat(pathname: string): ExplorerMediaFormat {
    if (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS}/`) ||
        pathname === ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST}/`)
    ) {
        return 'livestreams';
    }
    if (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_CLIPS}/`) ||
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`) ||
        isExplorerClipsBrandPath(pathname) ||
        pathname === ROUTES_NAMES.EXPLORER_SOCIAL_LIST ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_SOCIAL_LIST}/`)
    ) {
        return 'clips';
    }
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_LIVESTREAMS_TAB) return 'livestreams';
    if (tab === EXPLORER_SOCIAL_TAB) return 'clips';
    return 'ondemand';
}

export type ExplorerListTitleKey =
    | 'tabs.panel'
    | 'tabs.channels'
    | 'format.ondemand'
    | 'format.livestreams'
    | 'format.clips';

/** Page h1: format name on Canales / Auspiciadores lists; Mi panel stays the panel title. */
export function getExplorerListTitleKey(
    pathname: string,
    sponsorsFormat: 'ondemand' | 'clips' = 'ondemand',
): ExplorerListTitleKey {
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_GENERAL_TAB) return 'tabs.panel';
    if (isExplorerCanalesHubPath(pathname)) return 'tabs.channels';
    const pathSponsors = getExplorerSponsorsFormatFromPath(pathname);
    if (pathSponsors === 'clips' || (tab === EXPLORER_BRANDS_TAB && sponsorsFormat === 'clips')) {
        return 'format.clips';
    }
    if (pathSponsors === 'ondemand') return 'format.ondemand';
    const format = getExplorerMediaFormat(pathname);
    if (format === 'livestreams') return 'format.livestreams';
    if (format === 'clips') return 'format.clips';
    return 'format.ondemand';
}

/** Sidebar highlight: under Clips IA, livestreams and clip-channel pages belong to Canales. */
export function getActiveExplorerPillar(pathname: string, clipsIa: boolean): number {
    if (!clipsIa) return getActiveExplorerTab(pathname);
    if (isExplorerCanalesHubPath(pathname) || isExplorerCanalesFormatPath(pathname)) {
        return EXPLORER_PROGRAMS_TAB;
    }
    if (isExplorerAuspiciadoresFormatPath(pathname) || isExplorerClipsBrandPath(pathname)) {
        return EXPLORER_BRANDS_TAB;
    }
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_LIVESTREAMS_TAB || tab === EXPLORER_SOCIAL_TAB) {
        return EXPLORER_PROGRAMS_TAB;
    }
    return tab;
}

/** Clips IA can open Auspiciadores for clip brands even without explorer-sponsors. */
export function canOpenExplorerSection(
    tabIndex: number,
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): boolean {
    if (getAccessibleExplorerTabs(hasAccess).some((tab) => tab.index === tabIndex)) return true;
    return clipsIa && tabIndex === EXPLORER_BRANDS_TAB && hasAccess(EXPLORER_SOCIAL_TAB);
}

export const EXPLORER_PANEL_TAB = EXPLORER_TABS.find((tab) => tab.index === EXPLORER_GENERAL_TAB)!;

/** Mi panel opens for anyone with at least one Explorer section. */
export function canAccessExplorerPanel(hasAccess: HasExplorerTabAccess): boolean {
    return EXPLORER_PANEL_TAB.canAccess(hasAccess);
}

/** Which Explorer section a path belongs to: a section URL, or the section owning a detail page. */
export function getActiveExplorerTab(pathname: string): number {
    const section = EXPLORER_TABS.find((tab) => pathname === tab.path);
    if (section) return section.index;
    if (
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND ||
        pathname === ROUTES_NAMES.EXPLORER_BRANDS_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_ONDEMAND}/`) ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_BRANDS_CLIPS}/`)
    ) {
        return EXPLORER_BRANDS_TAB;
    }
    if (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_ONDEMAND}/`)
    ) {
        return EXPLORER_PROGRAMS_TAB;
    }
    if (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_LIVESTREAMS}/`)
    ) {
        return EXPLORER_LIVESTREAMS_TAB;
    }
    if (
        pathname === ROUTES_NAMES.EXPLORER_CANALES_CLIPS ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES_CLIPS}/`)
    ) {
        return EXPLORER_SOCIAL_TAB;
    }
    const base = `${ROUTES_NAMES.EXPLORER}/`;
    const sub = pathname.startsWith(base) ? pathname.slice(base.length).split('/')[0] : '';
    if (sub === 'canales') return EXPLORER_PROGRAMS_TAB;
    if (sub === 'auspiciadores' || sub === 'brands') return EXPLORER_BRANDS_TAB;
    if (sub === 'programs' || sub === 'podcasts') return EXPLORER_PROGRAMS_TAB;
    if (sub === 'clips' || sub === 'social') return EXPLORER_SOCIAL_TAB;
    if (sub === 'livestreams') return EXPLORER_LIVESTREAMS_TAB;
    return EXPLORER_GENERAL_TAB;
}
